import { Check, ChevronRight, ChevronLeft, Camera, CheckCircle2, Plus, ArrowRight } from "lucide-react";

export const Shell = ({ children }) => (
  <div className="min-h-screen bg-sand-50 sm:flex sm:items-center sm:justify-center sm:bg-ink-700 sm:p-8">
    <div className="mx-auto min-h-screen w-full max-w-[460px] bg-sand-50 px-5 pb-12 pt-7 sm:min-h-[740px] sm:rounded-[32px] sm:px-7 sm:shadow-[0_40px_80px_-30px_rgba(0,0,0,0.55)]">
      {children}
    </div>
  </div>
);

export const Head = ({ community, name, sub }) => (
  <div data-testid="work-head">
    <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-redline-600">{community}</div>
    <h1 className="mt-1.5 font-heading text-[26px] font-bold leading-tight tracking-tight text-ink">{name}</h1>
    {sub && <div className="mt-1 text-[13px] text-ink/55">{sub}</div>}
  </div>
);

export const BackLink = ({ label, onClick }) => (
  <button onClick={onClick} data-testid="work-back"
    className="mb-3 inline-flex items-center gap-1 text-[13px] font-semibold text-ink/45 transition-colors hover:text-ink">
    <ChevronLeft className="h-4 w-4" /> {label}
  </button>
);

export const Progress = ({ done, total }) => (
  <div className="mt-3 h-[5px] overflow-hidden rounded-full bg-sand-200" data-testid="work-progress">
    <div className="h-full rounded-full bg-emerald-700 transition-[width] duration-300"
      style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
  </div>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-2xl border border-sand-200 bg-white px-4 py-3.5 ${className}`} {...rest}>{children}</div>
);

export const AreaRow = ({ area, ofWord = "of", onClick }) => (
  <button onClick={onClick} data-testid={`work-area-${area.name}`}
    className="flex w-full items-center gap-3 rounded-2xl border border-sand-200 bg-white px-4 py-4 text-left transition-colors active:bg-sand-100">
    <span className="min-w-0 flex-1">
      <span className="block font-heading text-[16.5px] font-bold tracking-tight text-ink">{area.name}</span>
    </span>
    <span className="shrink-0 text-[12.5px] text-ink/45">{area.done} {ofWord} {area.total}</span>
    {area.done === area.total
      ? <Check className="h-4 w-4 shrink-0 text-emerald-700" strokeWidth={3} />
      : <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />}
  </button>
);

export const Tick = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 transition-transform active:scale-90 ${
      on ? "border-emerald-700 bg-emerald-700" : "border-sand-300 bg-white"}`}>
    {on && <Check className="h-4 w-4 text-white" strokeWidth={3.4} />}
  </button>
);

export const PrimaryButton = ({ children, tone = "ink", disabled, onClick, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`mt-6 w-full rounded-2xl py-4 text-[15.5px] font-bold transition-colors ${
      disabled ? "bg-sand-200 text-sand-500"
        : tone === "green" ? "bg-emerald-700 text-white active:bg-emerald-800" : "bg-ink text-white active:bg-ink-700"}`}>
    {children}
  </button>
);

export const QuestionLine = ({ text, label, hint, onClick }) => (
  <button onClick={onClick} data-testid="work-question-line"
    className="mt-4 flex w-full items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-left">
    <span className="min-w-0 flex-1">
      <span className="block text-[11px] font-bold uppercase tracking-wide text-amber-800">{label}</span>
      <span className="mt-0.5 block truncate text-[13px] text-amber-900">{text}</span>
      <span className="mt-0.5 block text-[11.5px] text-amber-700/80">{hint}</span>
    </span>
    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-amber-700" />
  </button>
);

export const Filed = ({ t, visit, next, onThread, questionLabel }) => (
  <div className="pt-6 text-center" data-testid="work-filed">
    <div className={`mx-auto grid h-16 w-16 place-items-center rounded-full border-2 ${
      visit.carried ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
      {visit.carried ? <ArrowRight className="h-8 w-8 text-amber-700" strokeWidth={2.2} />
        : <CheckCircle2 className="h-8 w-8 text-emerald-700" strokeWidth={2.2} />}
    </div>
    <h2 className="mt-4 font-heading text-[22px] font-bold tracking-tight text-ink">
      {visit.carried ? t.carriedHead : t.filed}
    </h2>
    <p className="mx-auto mt-1.5 max-w-[32ch] text-[13.5px] leading-relaxed text-ink/55">
      {visit.counts.photos && visit.note ? t.filedSub
        : visit.counts.photos ? t.filedSubPhotos
          : visit.note ? t.filedSubNote : t.filedSubPlain}
    </p>
    <Card className="mt-6 text-left">
      {[[t.tasksDone, `${visit.counts.done} ${t.of} ${visit.counts.total}`],
        [t.photos, `${visit.counts.photos}`],
        [t.onSite, `${visit.started_at}${visit.submitted_at ? ` – ${visit.submitted_at}` : ""}`]].map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-sand-100 py-2 text-[13px] last:border-0">
          <span className="text-ink/55">{k}</span><b className="font-semibold text-ink">{v}</b>
        </div>
      ))}
      {visit.tasks.filter((x) => !x.done).length > 0 && (
        <div className="flex justify-between pt-2 text-[13px]">
          <span className="text-ink/55">{t.couldntFinish}</span>
          <b className="font-semibold text-amber-800">{visit.tasks.filter((x) => !x.done).length}</b>
        </div>
      )}
    </Card>
    {visit.open_question && (
      <QuestionLine label={questionLabel || t.officeAskedPlain} text={visit.open_question} hint={t.tapToReply} onClick={onThread} />
    )}
    {next && <p className="mt-5 text-[12px] text-ink/40">{t.nextVisit}: {next.date} · {next.tasks} {t.tasks}</p>}
  </div>
);

export const PhotoStrip = ({ photos, onAdd, addLabel }) => (
  <div className="mt-2.5 flex flex-wrap gap-2">
    {photos.map((u, i) => <img key={i} src={u} alt="" className="h-14 w-14 rounded-xl object-cover" />)}
    {onAdd && (
      <button onClick={onAdd} data-testid="work-photo-add-more" aria-label={addLabel}
        className="grid h-14 w-14 place-items-center rounded-xl border-2 border-dashed border-sand-300 text-sand-500 active:bg-sand-100">
        <Plus className="h-5 w-5" />
      </button>
    )}
  </div>
);

// The office pinned something to this day — it sits above the work, before they start, because a
// heads-up buried in a conversation is a heads-up nobody reads.
export const PinnedNotes = ({ notes, t, onAck }) => !notes?.length ? null : (
  <div className="mt-4 space-y-2" data-testid="work-pinned">
    {notes.map((n) => (
      <div key={n.id} data-testid={`work-pinned-${n.id}`}
        className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
        <div className="text-[10.5px] font-bold uppercase tracking-wide text-amber-800">{t.noteFromOffice}</div>
        {n.text && <p className="mt-1 text-[14px] font-semibold leading-snug text-ink">{n.text}</p>}
        {!!n.photos?.length && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {n.photos.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer">
                <img src={u} alt="" className="h-20 w-20 rounded-xl object-cover" />
              </a>
            ))}
          </div>
        )}
        {n.ack ? (
          <div className="mt-2 flex items-center gap-1 text-[12px] font-semibold text-emerald-700"
            data-testid={`work-pinned-acked-${n.id}`}>
            <Check className="h-3.5 w-3.5" /> {t.gotItDone} · {n.ack.at}
          </div>
        ) : onAck ? (
          <button onClick={() => onAck(n.id)} data-testid={`work-pinned-ack-${n.id}`}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-bold text-white active:scale-[0.98]">
            <Check className="h-3.5 w-3.5" /> {t.gotIt}
          </button>
        ) : null}
      </div>
    ))}
  </div>
);

// One message in the conversation with the office — the same bubble on the open-visit screen and
// on the thread screen, so the office's photos can never show up on one and not the other.
export const ThreadMsg = ({ e, t, tasks }) => {
  const mine = e.side === "vendor";
  const shots = [...(e.photo ? [e.photo] : []), ...(e.photos || [])];
  // Which task the office means, and whether it's work or just a remark — a bare "the lobby was
  // missed" with no task and no re-do tag leaves the crew guessing on a 20-line list.
  const about = e.task_id && (tasks || []).find((x) => x.id === e.task_id)?.label;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`work-msg-${e.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${mine ? "bg-ink text-white" : "border border-sand-200 bg-white"}`}>
        <div className={`text-[10.5px] font-bold uppercase tracking-wide ${mine ? "text-white/50" : "text-ink/35"}`}>
          {mine ? t.you : t.office} · {e.at}
          {e.kind === "redo" && <span className="ml-1.5 text-redline-600">· {t.redoTag}</span>}
        </div>
        {about && (
          <div className={`mt-0.5 text-[11.5px] font-semibold ${mine ? "text-white/70" : "text-ink/50"}`}
            data-testid={`work-msg-task-${e.id}`}>
            {t.aboutTask} {about}
          </div>
        )}
        {e.text && <p className={`mt-1 text-[13.5px] leading-snug ${mine ? "text-white" : "text-ink-700"}`}>{e.text}</p>}
        {shots.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`work-msg-photos-${e.id}`}>
            {shots.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer">
                <img src={u} alt="" className="h-20 w-20 rounded-xl object-cover" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// One task as it stands on a visit nobody can change any more (or cannot change YET).
const RecapTask = ({ t, task, upcoming }) => (
  <div data-testid={`work-recap-task-${task.id}`}
    className={`mb-1.5 flex items-start gap-3 rounded-2xl border px-4 py-3 ${
      upcoming ? "border-sand-200 bg-white"
        : task.done ? "border-emerald-200 bg-emerald-50/60"
          : task.skipped ? "border-sand-200 bg-sand-100/60" : "border-amber-200 bg-amber-50/60"}`}>
    <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 ${
      upcoming ? "border-sand-300 bg-white" : task.done ? "border-emerald-700 bg-emerald-700" : "border-sand-300 bg-white"}`}>
      {!upcoming && task.done && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.4} />}
      {!upcoming && !task.done && <span className="h-[2px] w-2.5 rounded-full bg-sand-400" />}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[14.5px] font-semibold leading-snug text-ink">{task.label}</span>
      {task.added_by && <span className="mt-0.5 block text-[11.5px] text-ink/40">{t.addedBy} {task.added_by}</span>}
      {!upcoming && task.done && task.at && <span className="mt-0.5 block text-[11.5px] text-ink/40">{task.at}</span>}
      {!upcoming && task.skipped && <span className="mt-0.5 block text-[11.5px] text-ink/50">{t.told}: {task.reason}</span>}
      {!upcoming && !task.done && !task.skipped && <span className="mt-0.5 block text-[11.5px] text-amber-800">{t.notDone}</span>}
      {(task.steps || []).length > 0 && (
        <span className="mt-1.5 block space-y-0.5">
          {task.steps.map((s) => (
            <span key={s.id} className="flex items-center gap-2">
              <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${
                !upcoming && s.done ? "border-emerald-700 bg-emerald-700" : "border-sand-300 bg-white"}`}>
                {!upcoming && s.done && <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />}
              </span>
              <span className="text-[12.5px] text-ink-700">{s.label}</span>
            </span>
          ))}
        </span>
      )}
      {task.note && (
        <span className="mt-1.5 block rounded-xl bg-sand-100/80 px-3 py-2 text-[12.5px] text-ink-700">{task.note}</span>
      )}
      {task.photo_required && (upcoming || (task.done && !task.photos.length)) && (
        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-sand-100 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-ink/50">
          <Camera className="h-3 w-3" /> {upcoming ? t.photoRequired : t.noPhoto}
        </span>
      )}
      {task.photos.length > 0 && <PhotoStrip photos={task.photos} />}
    </span>
  </div>
);

// The whole visit, read only: the numbers (past only), every area with its tasks, and the note.
export const VisitRecap = ({ t, visit, upcoming }) => (
  <div className="mt-5" data-testid="work-recap">
    {!upcoming && (
      <Card className="mb-4">
        {[[t.tasksDone, `${visit.counts.done} ${t.of} ${visit.counts.total}`],
          [t.photos, `${visit.counts.photos}`],
          [t.onSite, visit.started_at ? `${visit.started_at}${visit.submitted_at ? ` – ${visit.submitted_at}` : ""}` : "—"]].map(([k, val]) => (
          <div key={k} className="flex justify-between border-b border-sand-100 py-2 text-[13px] last:border-0">
            <span className="text-ink/55">{k}</span><b className="font-semibold text-ink">{val}</b>
          </div>
        ))}
      </Card>
    )}
    {visit.sections.map((s) => (
      <div key={s.name} className="mb-4">
        <div className="mb-1.5 flex items-baseline justify-between">
          <span className="font-heading text-[15px] font-bold tracking-tight text-ink">{s.name}</span>
          {!upcoming && <span className="text-[11.5px] text-ink/45">{s.done} {t.of} {s.total}</span>}
        </div>
        {visit.tasks.filter((x) => x.section === s.name).map((task) => (
          <RecapTask key={task.id} t={t} task={task} upcoming={upcoming} />
        ))}
      </div>
    ))}
    {visit.note && (
      <Card data-testid="work-recap-note">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink/35">{t.note}</div>
        <p className="mt-1.5 whitespace-pre-wrap text-[14px] leading-relaxed text-ink">{visit.note}</p>
      </Card>
    )}
  </div>
);

export const PhotoPrompt = ({ t, onPick, testid }) => (
  <div className="mt-2.5">
    <div className="text-[10.5px] font-bold uppercase tracking-wide text-amber-800">{t.photoNeeded}</div>
    <button onClick={onPick} data-testid={testid}
      className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-amber-300 bg-white py-3 text-[13px] font-semibold text-ink/70 active:bg-amber-50">
      <Camera className="h-4 w-4" /> {t.takePhoto}
    </button>
  </div>
);
